import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import usePageMeta from "../hooks/usePageMeta";
import { BASE_URL } from "../config";
import {
  IDENTIFICATION_ACTION,
  recordIdentificationAction,
} from "./identificationActionApi";

function extractProfileImageVersion(profileImageUrl) {
  if (!profileImageUrl || typeof profileImageUrl !== "string") return null;
  const m = profileImageUrl.match(/\/(\d+)\.(?:png|jpe?g|webp)(?:\?|$)/i);
  const n = m ? Number(m[1]) : NaN;
  return Number.isFinite(n) && n > 0 ? n : null;
}

function staffAvatarDisplaySrc(accountId, profileImageUrl) {
  const id = Number(accountId);
  const base = BASE_URL;
  if (Number.isFinite(id) && id > 0 && base) {
    const v = extractProfileImageVersion(profileImageUrl);
    return v ? `${base}/api/avatars/account/${id}?v=${v}` : `${base}/api/avatars/account/${id}`;
  }
  if (profileImageUrl && String(profileImageUrl).trim()) {
    return String(profileImageUrl).trim();
  }
  return "";
}

function staffInitials(firstName, lastName) {
  const a = String(firstName || "").trim().charAt(0);
  const b = String(lastName || "").trim().charAt(0);
  const s = `${a}${b}`.toUpperCase();
  return s || "?";
}

export default function QRFaceNotIdentified() {
  usePageMeta("Face Not Identified");
  const navigate = useNavigate();
  const location = useLocation();
  const { qrData, capturedImage, reason, message, assignedStaff, identificationId } =
    location.state || {};
  const notFoundRecordedRef = useRef(false);

  useEffect(() => {
    if (!identificationId || notFoundRecordedRef.current) return;
    notFoundRecordedRef.current = true;
    recordIdentificationAction(identificationId, {
      action: IDENTIFICATION_ACTION.NOT_FOUND,
    }).then((ok) => {
      if (!ok) notFoundRecordedRef.current = false;
    });
  }, [identificationId]);

  const handleNeedHelp = async () => {
    await recordIdentificationAction(identificationId, {
      action: IDENTIFICATION_ACTION.NEED_HELP,
      status: "I NEED HELP",
    });
    navigate("/checkin/help" + window.location.search);
  };

  const handleTryAgain = () => {
    // Go back to face scan with QR data and other search parameters
    const urlParams = new URLSearchParams(window.location.search);
    if (qrData?.token) {
      urlParams.set('qrToken', qrData.token);
    }
    navigate(`/checkin/scan?${urlParams.toString()}`, {
      state: { qrData }
    });
  };

  const handleCancel = () => {
    // Go back to home preserving search params
    navigate(`/checkin${window.location.search}`);
  };

  if (!qrData) {
    navigate("/checkin" + window.location.search);
    return null;
  }

  return (
    <div className="h-screen bg-gray-50 flex items-center justify-center p-3 overflow-hidden">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6">
        {/* Error Icon */}
        <div className="flex justify-center mb-4">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center">
            <svg
              className="w-12 h-12 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
        </div>

        <h1 className="text-xl font-bold text-center mb-2 text-red-600">
          Face Not Identified
        </h1>

        <div className="text-center mb-6">
          <p className="text-gray-700 mb-4">
            {message || "We couldn't identify you from our staff database. Please try again with better lighting and positioning."}
          </p>

          {/* Side-by-side comparison or single captured image */}
          {assignedStaff ? (
            <div className="mb-5 bg-red-50/50 p-4 rounded-xl border border-red-100">
              <div className="text-xs font-semibold text-gray-500 mb-3 text-center uppercase tracking-wider">
                Comparison: Scanned vs. Assigned Staff
              </div>
              <div className="flex justify-center items-center gap-6 mb-3">
                {/* Captured Face */}
                <div className="flex flex-col items-center">
                  <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-red-300 shadow-sm bg-black">
                    {capturedImage ? (
                      <img
                        src={capturedImage}
                        alt="Captured face"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-400">
                        No Image
                      </div>
                    )}
                  </div>
                  <span className="text-xs font-medium text-gray-500 mt-1">Scanned Face</span>
                </div>

                <div className="text-gray-300 font-bold text-sm select-none">VS</div>

                {/* Expected Staff */}
                <div className="flex flex-col items-center">
                  <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-blue-300 shadow-sm bg-gray-100 flex items-center justify-center">
                    {assignedStaff.profileImageUrl ? (
                      <img
                        src={staffAvatarDisplaySrc(assignedStaff.accountId, assignedStaff.profileImageUrl)}
                        alt="Assigned Staff Profile"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = 'none';
                          const parent = e.target.parentElement;
                          const initialsSpan = document.createElement('span');
                          initialsSpan.className = "text-xl font-semibold text-gray-500 select-none";
                          initialsSpan.innerText = staffInitials(assignedStaff.firstName, assignedStaff.lastName);
                          parent.appendChild(initialsSpan);
                        }}
                      />
                    ) : (
                      <span className="text-xl font-semibold text-gray-500 select-none">
                        {staffInitials(assignedStaff.firstName, assignedStaff.lastName)}
                      </span>
                    )}
                  </div>
                  <span className="text-xs font-medium text-gray-500 mt-1">Assigned Staff</span>
                </div>
              </div>
              <div className="text-center text-sm font-bold text-gray-800">
                Expected: {assignedStaff.firstName} {assignedStaff.lastName}
              </div>
            </div>
          ) : (
            capturedImage && (
              <div className="flex justify-center mb-4">
                <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-red-300 shadow-sm">
                  <img
                    src={capturedImage}
                    alt="Captured face"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            )
          )}

          {/* Reason */}
          {reason && (
            <div className="bg-red-50 p-3 rounded-lg mb-4">
              <div className="text-sm text-red-800">
                <div className="font-semibold">Reason:</div>
                <div>{reason === "NO_CONFIDENT_MATCH" ? "No confident match found" :
                      reason === "FACE_NOT_MATCHED" ? "Face not matched" :
                      reason === "NO_PROFILE_PICTURE" ? "No profile picture found" :
                      "Face recognition failed"}</div>
              </div>
            </div>
          )}

          {/* QR Details */}
          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="text-sm text-gray-800">
              <div className="font-semibold">Check-in Location:</div>
              <div>{qrData.clientDetails?.clientName || "Unknown Location"}</div>
              {qrData.clientDetails?.clientAddress && (
                <div className="text-gray-600">{qrData.clientDetails.clientAddress}</div>
              )}
            </div>
          </div>
        </div>

        {/* Tips */}
        <div className="bg-blue-50 p-3 rounded-lg mb-6 text-sm text-blue-800">
          <div className="font-semibold mb-1">Tips for better identification:</div>
          <ul className="list-disc list-inside space-y-1">
            <li>Ensure your face is well-lit</li>
            <li>Remove glasses if possible</li>
            <li>Look directly at the camera</li>
            <li>Keep a neutral expression</li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="space-y-2">
          <button
            onClick={handleTryAgain}
            className="w-full bg-[#DC1F00] text-white py-3 px-4 rounded-lg font-semibold hover:bg-[#b91900] transition-colors"
          >
            TRY AGAIN
          </button>

          <button
            onClick={handleNeedHelp}
            className="w-full bg-gray-300 text-gray-700 py-3 px-4 rounded-lg font-semibold hover:bg-gray-400 transition-colors"
          >
            I NEED HELP
          </button>

          <button
            onClick={handleCancel}
            className="w-full bg-gray-200 text-gray-700 py-3 px-4 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
          >
            CANCEL CHECK-IN
          </button>
        </div>
      </div>
    </div>
  );
}
